import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Search, Shield, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (actionFilter) params.action_type = actionFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await api.get('/audit-log', { params });
      setLogs(response.data.logs);
    } catch (err) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, dateFrom, dateTo]);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString('en-PK', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getActionBadge = (action) => {
    const colors = {
      'Login': 'bg-blue-100 text-blue-700',
      'Logout': 'bg-gray-100 text-gray-700',
      'User_Created': 'bg-green-100 text-green-700',
      'User_Deactivated': 'bg-red-100 text-red-700',
      'Attendance_Correction': 'bg-orange-100 text-orange-700',
      'Report_Unlock': 'bg-purple-100 text-purple-700',
      'Report_Viewed': 'bg-indigo-100 text-indigo-700',
      'Report_Exported': 'bg-teal-100 text-teal-700',
      'Report_Submitted': 'bg-green-100 text-green-700',
      'Geo_fence_Updated': 'bg-yellow-100 text-yellow-700',
      'District_Created': 'bg-blue-100 text-blue-700',
      'Institution_Created': 'bg-blue-100 text-blue-700',
      'Institution_Deleted': 'bg-red-100 text-red-700',
      'Attendance_Viewed': 'bg-indigo-100 text-indigo-700',
      'Dashboard_Viewed': 'bg-gray-100 text-gray-600',
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout title="Audit Log">

      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3">
        <Shield size={24} className="text-blue-800" />
        <div>
          <p className="font-semibold text-blue-800">System Audit Trail</p>
          <p className="text-blue-600 text-sm">
            All system actions are permanently recorded. Records cannot be deleted.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Actions</option>
          <option value="Login">Login</option>
          <option value="Logout">Logout</option>
          <option value="User_Created">User Created</option>
          <option value="User_Deactivated">User Deactivated</option>
          <option value="Attendance_Correction">Attendance Correction</option>
          <option value="Report_Unlock">Report Unlock</option>
          <option value="Report_Viewed">Report Viewed</option>
          <option value="Report_Submitted">Report Submitted</option>
          <option value="Geo_fence_Updated">Geo-fence Updated</option>
          <option value="District_Created">District Created</option>
          <option value="Institution_Created">Institution Created</option>
          <option value="Institution_Deleted">Institution Deleted</option>
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          onClick={() => { setActionFilter(''); setDateFrom(''); setDateTo(''); }}
          className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
        >
          Clear Filters
        </button>

        <button
          onClick={() => toast.success('Export feature coming soon!')}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 ml-auto"
        >
          <Download size={16} />
          Export
        </button>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Date & Time</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">User</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Action</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Table</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Record ID</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">IP Address</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    Loading audit log...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No audit records found
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => (
                  <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">
                        {log.user_name || '—'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {log.user_role || ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadge(log.action_type)}`}>
                        {log.action_type?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {log.table_affected || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {log.record_id || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {log.ip_address || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
          Total: {logs.length} records
        </div>
      </div>

    </Layout>
  );
};

export default AuditLog;